import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Send, Users, Eye, Search, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const AvaliacaoGestor180 = () => {
  const navigate = useNavigate();
  const { cicloId } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [ciclo, setCiclo] = useState(null);
  const [competencias, setCompetencias] = useState([]);
  const [subordinados, setSubordinados] = useState([]);
  const [selectedColaborador, setSelectedColaborador] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [showComparacao, setShowComparacao] = useState(false);
  const [autoavaliacaoComparacao, setAutoavaliacaoComparacao] = useState(null);
  
  // Form data da avaliação
  const [respostas, setRespostas] = useState({});
  const [pontosFortes, setPontosFortes] = useState('');
  const [oportunidadesMelhoria, setOportunidadesMelhoria] = useState('');
  const [recomendacoesDesenvolvimento, setRecomendacoesDesenvolvimento] = useState('');

  useEffect(() => {
    fetchDados();
  }, [cicloId]);

  const fetchDados = async () => {
    try {
      const [cicloRes, compRes, empRes] = await Promise.all([
        axios.get(`${API}/api/feedback-180/ciclos/${cicloId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/feedback-180/competencias`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/employees`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      setCiclo(cicloRes.data);
      
      const competenciasCiclo = compRes.data.filter(c => 
        cicloRes.data.competencias_ids.includes(c.id)
      );
      setCompetencias(competenciasCiclo);

      // Filtrar apenas subordinados diretos do gestor
      // TODO: Implementar lógica de hierarquia real
      setSubordinados(empRes.data || []);
      
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao carregar dados');
    }
  };

  const handleSelectColaborador = async (colaborador) => {
    setSelectedColaborador(colaborador);
    
    // Buscar avaliação existente
    try {
      const avaliacaoRes = await axios.get(
        `${API}/api/feedback-180/avaliacao-gestor/ciclo/${cicloId}/colaborador/${colaborador.id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (avaliacaoRes.data) {
        const respostasMap = {};
        avaliacaoRes.data.respostas.forEach(r => {
          respostasMap[r.competencia_id] = {
            nota: r.nota,
            comentario: r.comentario || ''
          };
        });
        setRespostas(respostasMap);
        setPontosFortes(avaliacaoRes.data.pontos_fortes || '');
        setOportunidadesMelhoria(avaliacaoRes.data.oportunidades_melhoria || '');
        setRecomendacoesDesenvolvimento(avaliacaoRes.data.recomendacoes_desenvolvimento || '');
      } else {
        // Limpar form
        setRespostas({});
        setPontosFortes('');
        setOportunidadesMelhoria('');
        setRecomendacoesDesenvolvimento('');
      }
    } catch (error) {
      console.error('Erro ao buscar avaliação:', error);
    }
  };

  const handleCompararAutoavaliacao = async () => {
    try {
      const autoRes = await axios.get(
        `${API}/api/feedback-180/autoavaliacao/ciclo/${cicloId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { colaborador_id: selectedColaborador.id }
        }
      );
      
      setAutoavaliacaoComparacao(autoRes.data);
      setShowComparacao(true);
    } catch (error) {
      toast.error('Autoavaliação não disponível');
    }
  };

  const handleNotaChange = (compId, nota) => {
    setRespostas(prev => ({
      ...prev,
      [compId]: { ...prev[compId], nota, comentario: prev[compId]?.comentario || '' }
    }));
  };

  const handleComentarioChange = (compId, comentario) => {
    setRespostas(prev => ({
      ...prev,
      [compId]: { ...prev[compId], comentario, nota: prev[compId]?.nota || 0 }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (Object.keys(respostas).length < competencias.length) {
      toast.error('Por favor, avalie todas as competências');
      return;
    }

    try {
      setLoading(true);

      const respostasArray = Object.entries(respostas).map(([compId, dados]) => ({
        competencia_id: compId,
        nota: dados.nota,
        comentario: dados.comentario
      }));

      await axios.post(`${API}/api/feedback-180/avaliacao-gestor`, {
        ciclo_id: cicloId,
        colaborador_id: selectedColaborador.id,
        respostas: respostasArray,
        pontos_fortes: pontosFortes,
        oportunidades_melhoria: oportunidadesMelhoria,
        recomendacoes_desenvolvimento: recomendacoesDesenvolvimento
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Avaliação enviada com sucesso!');
      setSelectedColaborador(null);
      fetchDados();
    } catch (error) {
      toast.error('Erro ao enviar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColaborador = (colaborador) => {
    // TODO: Verificar se avaliação foi concluída
    return 'pendente'; // ou 'concluida'
  };

  const filteredSubordinados = subordinados.filter(sub => {
    const matchSearch = sub.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'todos' || getStatusColaborador(sub) === filterStatus;
    return matchSearch && matchStatus;
  });

  if (!ciclo) {
    return <div className="p-6">Carregando...</div>;
  }

  // Tela de lista de subordinados
  if (!selectedColaborador) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Button variant="outline" onClick={() => navigate('/feedback/cultural')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Avaliar Subordinados - {ciclo.nome}</CardTitle>
            <CardDescription>
              Selecione um colaborador para avaliar suas competências
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filtros */}
            <div className="flex gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar colaborador..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="concluida">Concluída</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Lista de Subordinados */}
            <div className="space-y-3">
              {filteredSubordinados.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Nenhum colaborador encontrado</p>
                </div>
              ) : (
                filteredSubordinados.map((colaborador) => {
                  const status = getStatusColaborador(colaborador);
                  
                  return (
                    <div
                      key={colaborador.id}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleSelectColaborador(colaborador)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-700 font-semibold text-lg">
                              {colaborador.nome?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          
                          <div>
                            <h3 className="font-semibold text-gray-900">{colaborador.nome}</h3>
                            <p className="text-sm text-gray-600">
                              {colaborador.cargo || 'Cargo não informado'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          {status === 'concluida' ? (
                            <div className="flex items-center space-x-2 text-green-600">
                              <CheckCircle2 className="h-5 w-5" />
                              <span className="text-sm font-medium">Concluída</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2 text-orange-600">
                              <Clock className="h-5 w-5" />
                              <span className="text-sm font-medium">Pendente</span>
                            </div>
                          )}
                          
                          <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                            Avaliar
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de avaliação do colaborador selecionado
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Button 
        variant="outline" 
        onClick={() => setSelectedColaborador(null)} 
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar para Lista
      </Button>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Avaliando: {selectedColaborador.nome}</CardTitle>
              <CardDescription>
                {selectedColaborador.cargo} • {ciclo.nome}
              </CardDescription>
            </div>
            
            <Button
              variant="outline"
              onClick={handleCompararAutoavaliacao}
              className="text-blue-600"
            >
              <Eye className="h-4 w-4 mr-2" />
              Comparar com Autoavaliação
            </Button>
          </div>
        </CardHeader>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        {competencias.map((comp, index) => (
          <Card key={comp.id}>
            <CardHeader>
              <CardTitle className="text-lg">
                {index + 1}. {comp.nome}
              </CardTitle>
              <CardDescription>{comp.descricao}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Como você avalia este colaborador nesta competência?</Label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((nota) => (
                    <button
                      key={nota}
                      type="button"
                      onClick={() => handleNotaChange(comp.id, nota)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                        respostas[comp.id]?.nota === nota
                          ? 'bg-purple-600 text-white border-purple-600 scale-110'
                          : 'border-gray-300 hover:border-purple-400'
                      }`}
                    >
                      {nota}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-600">
                  1 = Discordo totalmente | 5 = Concordo totalmente
                </p>
              </div>

              {ciclo.permitir_comentarios && (
                <div className="space-y-2">
                  <Label>Observações (opcional)</Label>
                  <Textarea
                    value={respostas[comp.id]?.comentario || ''}
                    onChange={(e) => handleComentarioChange(comp.id, e.target.value)}
                    placeholder="Adicione observações sobre esta competência..."
                    rows={2}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardHeader>
            <CardTitle>Avaliação Qualitativa</CardTitle>
            <CardDescription>
              Forneça feedback adicional para o desenvolvimento do colaborador
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Pontos Fortes Observados</Label>
              <Textarea
                value={pontosFortes}
                onChange={(e) => setPontosFortes(e.target.value)}
                placeholder="Descreva os pontos fortes que você observou..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Oportunidades de Melhoria</Label>
              <Textarea
                value={oportunidadesMelhoria}
                onChange={(e) => setOportunidadesMelhoria(e.target.value)}
                placeholder="Identifique áreas que precisam de desenvolvimento..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Recomendações para Desenvolvimento</Label>
              <Textarea
                value={recomendacoesDesenvolvimento}
                onChange={(e) => setRecomendacoesDesenvolvimento(e.target.value)}
                placeholder="Sugira ações específicas para o desenvolvimento..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => setSelectedColaborador(null)}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading || Object.keys(respostas).length < competencias.length}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Send className="h-4 w-4 mr-2" />
            {loading ? 'Enviando...' : 'Enviar Avaliação'}
          </Button>
        </div>
      </form>

      {/* Modal de Comparação */}
      <Dialog open={showComparacao} onOpenChange={setShowComparacao}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Comparação: Autoavaliação x Sua Avaliação</DialogTitle>
            <DialogDescription>
              Veja como o colaborador se autoavaliou em cada competência
            </DialogDescription>
          </DialogHeader>
          
          {autoavaliacaoComparacao && (
            <div className="space-y-4">
              {competencias.map((comp) => {
                const autoResp = autoavaliacaoComparacao.respostas?.find(
                  r => r.competencia_id === comp.id
                );
                const gestorResp = respostas[comp.id];
                
                return (
                  <div key={comp.id} className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">{comp.nome}</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Autoavaliação:</p>
                        <div className="flex items-center space-x-2">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-900 font-bold text-lg">
                              {autoResp?.nota || '-'}
                            </span>
                          </div>
                          {autoResp?.comentario && (
                            <p className="text-xs text-gray-600 flex-1">
                              {autoResp.comentario}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Sua Avaliação:</p>
                        <div className="flex items-center space-x-2">
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-900 font-bold text-lg">
                              {gestorResp?.nota || '-'}
                            </span>
                          </div>
                          {gestorResp?.comentario && (
                            <p className="text-xs text-gray-600 flex-1">
                              {gestorResp.comentario}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvaliacaoGestor180;
